import type { MessageMap, WorkerRequestId, WorkerResponse } from './utils.js';

type WorkerClientFactory<TMap extends MessageMap> = {
  post: <TType extends keyof TMap>(type: TType, payload: TMap[TType]['payload']) => Promise<TMap[TType]['response']>;

  subscribe: (handler?: (message: Pick<WorkerResponse<TMap>, 'type' | 'payload'>) => void) => () => void;

  terminate: () => void;
};

const getId = (): WorkerRequestId => {
  return crypto.randomUUID() as WorkerRequestId;
};

/**
 * Creates a client interface for communicating with a web worker using typed messages.
 *
 * @template TMessageMap - The message map type defining allowed message types and payloads
 * @param worker - The web worker instance to communicate with
 * @returns Web worker API with methods to post messages, subscribe to responses, and terminate the worker
 */
export function createWorkerClient<TMessageMap extends MessageMap>(worker: Worker): WorkerClientFactory<TMessageMap> {
  const pending = new Map<WorkerRequestId, (value: unknown) => void>();

  const onMessage = (event: MessageEvent<WorkerResponse<TMessageMap>>): void => {
    const message = event.data;

    const resolver = pending.get(message.id);
    if (resolver) {
      resolver(message.payload);
      pending.delete(message.id);
    }
  };

  worker.addEventListener('message', onMessage);

  /**
   * Send a message to the web worker and returns a promise to be awaited for the response.
   *
   * @param type - The message type to send
   * @param payload - The payload data to send with the message
   * @returns A Promise that resolves with the worker's response
   */
  const post: WorkerClientFactory<TMessageMap>['post'] = (type, payload) => {
    const id = getId();
    const message = {
      id,
      type,
      payload,
    } satisfies WorkerResponse<TMessageMap>;

    return new Promise((resolve) => {
      pending.set(id, resolve);
      // eslint-disable-next-line unicorn/require-post-message-target-origin
      worker.postMessage(message);
    });
  };

  /**
   * Subscribe to messages from the web worker.
   *
   * @param onMessageHandler - Optional callback function that will be called when a message is received from the worker
   * @returns A function that can be called to unsubscribe from worker messages
   */
  const subscribe: WorkerClientFactory<TMessageMap>['subscribe'] = (onMessageHandler) => {
    const handler = (event: MessageEvent<Pick<WorkerResponse<TMessageMap>, 'type' | 'payload'>>): void => {
      onMessageHandler?.(event.data);
    };
    worker.addEventListener('message', handler);

    return () => {
      worker.removeEventListener('message', handler);
    };
  };

  /**
   * Terminate the web worker and clean up event listeners.
   * This should be called when the worker is no longer needed to prevent memory leaks.
   */
  const terminate: WorkerClientFactory<TMessageMap>['terminate'] = () => {
    worker.removeEventListener('message', onMessage);
    worker.terminate();
  };

  return {
    post,
    subscribe,
    terminate,
  };
}
